import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  Renderer2,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, AbstractControl } from '@angular/forms';
import * as _ from 'lodash';
import { pluck } from 'rxjs/operators';
import { ProjectService } from 'src/app/core/services/project.service';
import { Axle, CabinDimensions, LoadingSpaceType, ProjectOptions } from 'src/app/core/types';
import { CabinDimensionsGenerator } from 'src/app/core/utils/cabin-dimensions-generator';
import { Subscription } from 'rxjs';

export interface AxleValue {
  axleValue: number;
  index: number;
}

const DEFAULT_LOADINGSPACE_LENGTH = 450;
const LONGER_SPACE = 1360;

@Component({
    selector: 'gl-axle-loads',
    templateUrl: './axle-loads.component.html',
    styleUrls: ['./axle-loads.component.scss'],
    standalone: false
})
export class AxleLoadsComponent implements OnChanges, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('labelElement', { static: true }) public labelElement: ElementRef<HTMLElement>;
  @Input() public selectedAxle: number;
  @Input() public isBus = true;
  @Input() public axles: Axle[];
  @Input() public isSwitchSet: boolean;
  @Input() public loadingSpace: LoadingSpaceType;
  @Output() public valueOfAxle = new EventEmitter<AxleValue>();
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public unit: string;
  public inputRange = DEFAULT_LOADINGSPACE_LENGTH;
  private subscriptions: Subscription[] = [];
  public cabinLength = 0;
  public isSet = false;
  public numberOfAxles: number[] = [];
  public isPrint = false;

  constructor(
    private projectService: ProjectService,
    private el: ElementRef,
    public renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.setInputRange();
    this.setProjectVariables();
    this.setNumberOfAxles();
    this.addingControls();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.emitCurrentAxleValue();
    });
  }

  private setInputRange(): void {
    const labelElementDimensions: DOMRect = this.labelElement.nativeElement.getBoundingClientRect();
    this.inputRange = labelElementDimensions.width;
  }

  private setNumberOfAxles(): void {
    this.numberOfAxles = this.axles.map((_, index) => index);
    this.numberOfAxles =
      this.numberOfAxles.length > 0 ? this.numberOfAxles : !this.isBus ? [0, 1, 2] : [0, 1];
  }

  private setProjectVariables(): void {
    this.subscriptions.push(
      this.projectService.project
        .pipe(pluck('options'))
        .subscribe((options: ProjectOptions): void => {
          this.unit = options?.unit;
          const cabinDimensions: CabinDimensions = CabinDimensionsGenerator(
            options?.unit,
            this.loadingSpace,
          );
          this.setCabinLength(cabinDimensions);
          const labelLength: number = this.labelElement.nativeElement.getBoundingClientRect().width;
          const loadingSpaceLength: number = this.loadingSpace.length
            ? this.loadingSpace.length
            : DEFAULT_LOADINGSPACE_LENGTH;
          this.setAfterWidth(
            this.recalculateCabinLengthToInputRange(
              this.cabinLength,
              labelLength,
              loadingSpaceLength,
            ),
          );
          this.cdr.detectChanges();
        }),
    );
  }

  private setCabinLength(cabinDimensions: CabinDimensions): void {
    const cabinLength = this.unit === 'mm' ? cabinDimensions.length * 10 : cabinDimensions.length;
    this.cabinLength = this.isSwitchSet ? 0 : cabinLength;
  }

  private recalculateCabinLengthToInputRange(
    cabinLength: number,
    labelElementWidth: number,
    loadingSpaceLength: number,
  ): number {
    const totalLength: number = cabinLength + loadingSpaceLength;
    const proportion: number = labelElementWidth / totalLength;
    const cabinWidthInPixels: number = cabinLength * proportion;
    return Math.round(cabinWidthInPixels);
  }

  public setAfterWidth(newWidth: number): void {
    const frontLoad = this.el.nativeElement.querySelector('.frontLoad');
    if (!frontLoad) {
      return;
    }
    frontLoad.style.setProperty('--dynamic-width', `${Math.max(0, newWidth)}px`);
    void frontLoad.offsetWidth;
  }

  private emitCurrentAxleValue(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((): void => {
        const selectedAxle: number = this.selectedAxle - 1;
        if (this.form.controls[selectedAxle]) {
          const currentAxleValue: number = this.getRealAxleValue(selectedAxle);
          this.valueOfAxle.emit({ axleValue: currentAxleValue, index: selectedAxle });
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.axles) {
      this.setNumberOfAxles();
      this.addingControls();
    }
    if (changes.isSwitchSet !== undefined) {
      this.setProjectVariables();
      this.setNumberOfAxles();
      this.addingControls();
    }
    if (changes.selectedAxle) {
      this.updateDisabledControls();
    }
    if (changes.loadingSpace && changes.loadingSpace.currentValue) {
      this.loadingSpace = changes.loadingSpace.currentValue;
      this.setInputRange();
      this.setProjectVariables();
      this.setNumberOfAxles();
      this.addingControls();
      this.cdr.detectChanges();
    }
  }

  private safeNumber(value: number | undefined | null): number {
    if (value === undefined || value === null) {
      return 0;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) || !isFinite(num) ? 0 : num;
  }

  public getAxleValue(axle: number): number {
    const value = this.form.controls[axle] !== undefined
      ? (this.form.controls[axle].value as unknown as number)
      : 0;
    const safe = this.safeNumber(value);
    const max = this.safeNumber(this.inputRange);
    return Math.min(Math.max(0, safe), max);
  }

  public getSliderValueFromRealValue(realValue: number): number {
    const safeRealValue = this.safeNumber(realValue);
    const loadingSpaceLength: number = this.loadingSpace?.length
      ? this.safeNumber(this.loadingSpace.length)
      : DEFAULT_LOADINGSPACE_LENGTH;
    const containerLength: number = this.isSwitchSet
      ? this.safeNumber(this.loadingSpace?.length1) || loadingSpaceLength
      : loadingSpaceLength + this.safeNumber(this.cabinLength);
    const maxRange: number = this.safeNumber(this.inputRange) || DEFAULT_LOADINGSPACE_LENGTH;
    
    if (containerLength === 0 || maxRange === 0) {
      return 0;
    }
    
    const result = ((safeRealValue + this.safeNumber(this.cabinLength)) * maxRange) / containerLength;
    return this.safeNumber(result);
  }

  public getRealAxleValue(axle: number): number {
    const axleValue: number = this.getAxleValue(axle);
    const loadingSpaceLength: number = this.loadingSpace?.length
      ? this.safeNumber(this.loadingSpace.length)
      : DEFAULT_LOADINGSPACE_LENGTH;
    const containerLength: number = this.isSwitchSet
      ? this.safeNumber(this.loadingSpace?.length1) || loadingSpaceLength
      : loadingSpaceLength + this.safeNumber(this.cabinLength);
    const maxRange: number = this.safeNumber(this.inputRange) || DEFAULT_LOADINGSPACE_LENGTH;
    
    if (maxRange === 0) {
      return 0;
    }
    
    const value: number = (containerLength * axleValue) / maxRange - this.safeNumber(this.cabinLength);
    const rounded = _.round(value);
    return this.safeNumber(rounded);
  }

  public checkAxleValue(axle: number): boolean {
    const axleData = this.axles[axle];
    if (!axleData) return false;
    return axleData.distanceFromSpaceFront !== null && axleData.distanceFromSpaceFront !== undefined;
  }

  private isFirstAxleNull(axles: Axle[]): boolean {
    return axles[0].distanceFromSpaceFront === null;
  }

  private isLoadingSpaceLonger(loadingSpace: LoadingSpaceType): boolean {
    const loadingSpaceLength: number = loadingSpace.length
      ? loadingSpace.length
      : DEFAULT_LOADINGSPACE_LENGTH;
    switch (this.unit) {
      case 'cm':
        return loadingSpaceLength >= LONGER_SPACE;
      case 'mm':
        return loadingSpaceLength >= LONGER_SPACE * 10;
      case 'lb':
        return loadingSpaceLength >= LONGER_SPACE / 2.54;
      default:
        return false;
    }
  }

  private recalculateStaticValue(value: number): number {
    const mmValue: number = value * 10;
    const lbValue: number = value / 2.54;
    const isUnit: string = this.unit;
    if (!isUnit) {
      return value;
    }
    return this.unit === 'cm' ? value : this.unit === 'mm' ? mmValue : lbValue;
  }

  private calculateInputRange(): number {
    const areAxlesLengthMoreThan3: boolean = this.axles.length > 2;
    const isFirstAxleNull: boolean = this.isFirstAxleNull(this.axles);
    const isLoadingSpaceLonger: boolean = this.isLoadingSpaceLonger(this.loadingSpace);
    if (isFirstAxleNull && isLoadingSpaceLonger && areAxlesLengthMoreThan3) {
      const value: number = this.recalculateStaticValue(167);
      return this.getSliderValueFromRealValue(value);
    } else if (isFirstAxleNull && !areAxlesLengthMoreThan3) {
      const value: number = -this.recalculateStaticValue(
        (this.unit === 'mm' ? this.cabinLength * 0.1 : this.cabinLength) / 2,
      );
      return this.getSliderValueFromRealValue(value);
    } else {
      return this.inputRange * 0.1;
    }
  }

  public addingControls(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.subscriptions = [];
    this.form = new UntypedFormGroup({});
    const initialValues: number[] = [];
    this.axles.forEach((axle: Axle, index: number): void => {
      let value: number;
      if (axle.distanceFromSpaceFront !== null && axle.distanceFromSpaceFront !== undefined) {
        value = this.getSliderValueFromRealValue(axle.distanceFromSpaceFront);
      } else {
        if (index === 0) {
          value = this.calculateInputRange();
        } else if (index === 1) {
          value = this.inputRange * 0.75;
        } else {
          value = this.inputRange * 0.75 + index * 24;
        }
      }
      initialValues[index] = value;
    });

    this.axles.forEach((_, index: number): void => {
      const axleKey: string = index.toString();
      const isSelected: boolean = this.selectedAxle - 1 === index;
      const formControl: UntypedFormControl = new UntypedFormControl({
        value: initialValues[index],
        disabled: !isSelected,
      });
      this.form.addControl(axleKey, formControl);
    });
    this.emitCurrentAxleValue();
  }

  public updateDisabledControls(): void {
    if (!this.form) return;
    this.numberOfAxles.forEach((axleIndex: number): void => {
      const control: AbstractControl = this.form.get(axleIndex.toString());
      if (control) {
        const isSelected: boolean = this.selectedAxle - 1 === axleIndex;
        if (isSelected && control.disabled) {
          control.enable();
        } else if (!isSelected && control.enabled) {
          control.disable();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.numberOfAxles = [];
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }
}
